import { useEffect, useState } from 'react';

export default function HeroSection() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen bg-[#1A1208] text-white overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-96 h-96 bg-[#E8603C] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#E8603C] rounded-full blur-3xl"></div>
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-20 flex flex-col items-center text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full mb-8">
          <span className="text-[#E8603C]">✦</span>
          <span className="text-sm font-medium">For Real Estate Agents</span>
        </div>

        {/* Headline */}
        <h1 className="text-5xl md:text-7xl font-serif mb-6 max-w-5xl leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
          Stop Wondering What to Post.{' '}
          <span className="italic text-[#E8603C]">Start Closing More Clients.</span>
        </h1>

        {/* Subheadline */}
        <p className="text-lg md:text-xl text-white/80 max-w-3xl mb-12">
          The complete 30-day Instagram content system — done-for-you templates, captions, reel hooks, and your full posting calendar.
        </p>

        {/* 3D Book Mockup */}
        <div className="relative mb-12 group">
          <div className="relative w-80 h-96 perspective-1000">
            {/* Book cover with 3D effect */}
            <div className="relative w-full h-full transform transition-all duration-500 group-hover:scale-105 group-hover:rotate-y-5" style={{ transformStyle: 'preserve-3d' }}>
              {/* Main book face */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#E8603C] to-[#D14F2A] rounded-lg shadow-2xl flex flex-col items-center justify-center p-8 border-4 border-[#C04525]">
                {/* Book spine effect */}
                <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-black/40 to-transparent"></div>
                
                {/* Book content */}
                <div className="relative z-10 flex flex-col items-center justify-center h-full">
                  <div className="text-6xl mb-4">🏡</div>
                  <h3 className="text-2xl font-bold text-white mb-2 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
                    THE REALTOR<br />CONTENT ENGINE
                  </h3>
                  <div className="w-16 h-px bg-white/40 my-4"></div>
                  <p className="text-sm text-white/90 text-center font-medium">
                    30-Day Instagram System<br />for Real Estate Agents
                  </p>
                </div>

                {/* Corner decoration */}
                <div className="absolute top-4 right-4 w-12 h-12 border-t-2 border-r-2 border-white/30"></div>
                <div className="absolute bottom-4 left-4 w-12 h-12 border-b-2 border-l-2 border-white/30"></div>
              </div>

              {/* Book pages effect */}
              <div className="absolute inset-y-2 right-2 w-4 bg-white rounded-r-sm shadow-lg" style={{ transform: 'translateZ(-10px)' }}></div>
              <div className="absolute inset-y-3 right-3 w-4 bg-gray-100 rounded-r-sm shadow-md" style={{ transform: 'translateZ(-20px)' }}></div>
            </div>

            {/* Shadow */}
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-64 h-8 bg-black/30 blur-2xl rounded-full"></div>
          </div>

          {/* Floating animation */}
          <style>{`
            @keyframes float {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-10px); }
            }
            .perspective-1000 {
              perspective: 1000px;
            }
            .group:hover .transform {
              animation: float 3s ease-in-out infinite;
            }
          `}</style>
        </div>

        {/* CTA Button */}
        <a
          href="https://consultix6.gumroad.com/l/realtor-content-engine"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-8 py-4 bg-[#E8603C] text-white text-lg font-semibold rounded-full hover:bg-[#D14F2A] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 mb-8 whitespace-nowrap cursor-pointer"
        >
          Get Instant Access — $29
          <span className="text-xl">→</span>
        </a>

        {/* Trust Badges */}
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-white/80">
          <div className="flex items-center gap-2">
            <span className="text-[#E8603C] text-lg">✓</span>
            <span>30 Post Templates</span>
          </div>
          <span className="text-white/40">·</span>
          <div className="flex items-center gap-2">
            <span className="text-[#E8603C] text-lg">✓</span>
            <span>30 Written Captions</span>
          </div>
          <span className="text-white/40">·</span>
          <div className="flex items-center gap-2">
            <span className="text-[#E8603C] text-lg">✓</span>
            <span>30 Reel Hook Scripts</span>
          </div>
          <span className="text-white/40">·</span>
          <div className="flex items-center gap-2">
            <span className="text-[#E8603C] text-lg">✓</span>
            <span>30-Day Calendar</span>
          </div>
        </div>
      </div>
    </section>
  );
}